import React from 'react';
import { toast } from 'react-toastify';
import { useWallet } from '../../hooks/useWallet';
import { AppButton } from '../ui/AppButton';
import { DevAccountsBanner } from './DevAccountsBanner';

/**
 * Wallet connector with chain mismatch handling and balance display.
 */
export const WalletConnector = () => {
  const expectedChainId = Number(process.env.REACT_APP_CHAIN_ID || 31337);
  const {
    connectWallet,
    switchAccount,
    disconnectWallet,
    switchToExpectedNetwork,
    clearAccountSwitchState,
    addressDisplay,
    address,
    networkName,
    ethBalance,
    lbtBalance,
    stakeholderStatus,
    isConnected,
    isConnecting,
    isSwitchingAccount,
    accountSwitchState,
    isNetworkMismatch,
    chainId,
  } = useWallet();

  const statusToneByState = {
    switch_confirmed: 'success',
    switch_failed: 'error',
    manual_action_required: 'warning',
    account_unchanged: 'warning',
    wallet_prompt_open: 'info',
    switching_requested: 'info',
    connected: 'neutral',
    disconnected: 'neutral',
  };

  const statusLabelByState = {
    switch_confirmed: 'Account Updated',
    switch_failed: 'Switch Failed',
    manual_action_required: 'Manual Action Required',
    account_unchanged: 'Account Unchanged',
    wallet_prompt_open: 'Waiting for MetaMask',
    switching_requested: 'Request Sent',
    connected: 'Connected',
    disconnected: 'Disconnected',
  };

  const handleConnect = async () => {
    try {
      await connectWallet();
      clearAccountSwitchState();

      // Only attempt a switch when the wallet is actually on a different chain.
      try {
        const activeChainRaw = await window.ethereum.request({ method: 'eth_chainId' });
        const activeChainId =
          typeof activeChainRaw === 'string' && activeChainRaw.startsWith('0x')
            ? parseInt(activeChainRaw, 16)
            : Number(activeChainRaw);

        if (Number.isFinite(activeChainId) && activeChainId !== expectedChainId) {
          await switchToExpectedNetwork();
        }
      } catch (switchError) {
        // Non-fatal: user may have dismissed the network switch prompt.
        // The "Switch Chain" button remains visible for manual switching.
        const code = Number(switchError?.code ?? switchError?.error?.code);
        if (code === -32002) {
          toast.info('MetaMask already has a pending network request. Complete it, then retry if needed.');
        }
      }
      toast.success('Wallet connected');
    } catch (error) {
      toast.error(error.message || 'Failed to connect wallet');
    }
  };

  const handleSwitchNetwork = async () => {
    try {
      await switchToExpectedNetwork();
      toast.success('Network switched successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to switch network');
    }
  };

  const handleSwitchAccount = async () => {
    try {
      const switchResult = await switchAccount();
      if (!switchResult) return;

      if (switchResult.status === 'switch_confirmed') {
        toast.success('Active account updated. Data is now shown for the new account.');
        return;
      }

      if (switchResult.status === 'account_unchanged') {
        toast.warn('Account did not change. Open MetaMask and select a different account.');
        return;
      }

      if (switchResult.status === 'manual_action_required') {
        toast.info('Open MetaMask to finish account switching, then return to this page.');
        return;
      }

      if (switchResult.status === 'switch_failed') {
        toast.error(switchResult.message || 'Account switch failed.');
      }
    } catch (error) {
      toast.error(error.message || 'Unable to request account change.');
    }
  };

  const handleDisconnect = () => {
    disconnectWallet();
    toast.info('Wallet disconnected');
  };

  return (
    <section className="panel-card" aria-labelledby="wallet-connector-title">
      <div className="panel-header">
        <h2 id="wallet-connector-title">Wallet Connector</h2>
      </div>

      {/* Shown only in development builds — lists Hardhat accounts with copy buttons */}
      <DevAccountsBanner />

      {!isConnected ? (
        <AppButton
          type="button"
          onClick={handleConnect}
          loading={isConnecting}
          variant="primary"
        >
          {isConnecting ? 'Connecting...' : 'Connect Wallet'}
        </AppButton>
      ) : (
        <div className="wallet-connector-layout">
          <div className="wallet-connector-status-row">
            <span className={`wallet-connector-pill wallet-connector-pill--${statusToneByState[accountSwitchState.status] || 'neutral'}`}>
              {statusLabelByState[accountSwitchState.status] || 'Connected'}
            </span>
            <span className="wallet-connector-helper">
              {accountSwitchState.message || 'Connected and ready.'}
            </span>
          </div>

          <div className="wallet-connector-main-row">
            <div className="wallet-status-grid wallet-connector-info-grid" style={{ flex: 1 }}>
              <div>
                <span className="label">Active Account</span>
                <p>{addressDisplay}</p>
                <small className="wallet-connector-mono">{address}</small>
              </div>
              <div>
                <span className="label">Network</span>
                <p>{networkName} (chain {chainId})</p>
              </div>
              <div>
                <span className="label">Balance</span>
                <p>
                  {isNetworkMismatch
                    ? <span className="wallet-connector-warning-text">Switch network to view</span>
                    : `${Number(ethBalance).toFixed(4)} ETH`}
                </p>
              </div>
              <div>
                <span className="label">LBT Balance</span>
                <p>
                  {isNetworkMismatch
                    ? <span className="wallet-connector-warning-text">Switch network to view</span>
                    : `${lbtBalance} LBT`}
                </p>
              </div>
              {stakeholderStatus?.registered && (
                <div>
                  <span className="label">Stakeholder</span>
                  <p>{stakeholderStatus.verified ? 'Verified' : 'Pending verification'}</p>
                </div>
              )}
              {Boolean(accountSwitchState.previousAddress) && (accountSwitchState.status === 'switching_requested' || accountSwitchState.status === 'wallet_prompt_open') ? (
                <div>
                  <span className="label">Current App Account</span>
                  <p>{accountSwitchState.previousAddress}</p>
                  <small className="wallet-connector-helper">Waiting for MetaMask confirmation.</small>
                </div>
              ) : null}
            </div>

            <div className="wallet-connector-actions">
              {isNetworkMismatch && (
                <button type="button" onClick={handleSwitchNetwork} className="px-3 py-1.5 rounded-card-sm text-[12px] font-semibold text-lb-body hover:text-lb-ink hover:bg-black/[0.04] transition-colors focus-ring outline-none">
                  Switch Chain
                </button>
              )}
              <button
                type="button"
                onClick={handleSwitchAccount}
                className="px-3 py-1.5 rounded-card-sm text-[12px] font-semibold text-lb-body hover:text-lb-ink hover:bg-black/[0.04] transition-colors focus-ring outline-none"
                disabled={isConnecting || isSwitchingAccount}
              >
                {isSwitchingAccount ? 'Awaiting MetaMask confirmation...' : 'Request Account Change'}
              </button>
              <button type="button" onClick={handleDisconnect} disabled={isSwitchingAccount} className="px-3 py-1.5 rounded-card-sm text-[12px] font-semibold text-lb-body hover:text-lb-ink hover:bg-black/[0.04] transition-colors focus-ring outline-none">
                Disconnect
              </button>
            </div>
          </div>

          {(accountSwitchState.status === 'manual_action_required' || accountSwitchState.status === 'account_unchanged') && (
            <div className="wallet-connector-inline-warning" role="alert" aria-live="polite">
              <strong>Account not switched in app yet.</strong>
              <span>
                Open MetaMask, choose the account you want, then return to this page. LiveBetter Hub updates only after the provider confirms the new active account.
              </span>
            </div>
          )}

          {(accountSwitchState.status === 'switch_failed') && (
            <div className="wallet-connector-inline-error" role="alert" aria-live="polite">
              <strong>Could not switch account.</strong>
              <span>{accountSwitchState.message || 'Try again or reconnect your wallet.'}</span>
            </div>
          )}

          {accountSwitchState.status === 'switch_confirmed' && (
            <div className="wallet-connector-inline-success" role="status" aria-live="polite">
              <strong>Account confirmed.</strong>
              <span>Data panels now use the active account shown above.</span>
            </div>
          )}

          {accountSwitchState.status === 'switch_confirmed' && (
            <div style={{ marginTop: '0.3rem' }}>
              <button type="button" onClick={clearAccountSwitchState} className="px-3 py-1.5 rounded-card-sm text-[12px] font-semibold text-lb-body hover:text-lb-ink hover:bg-black/[0.04] transition-colors focus-ring outline-none">
                Dismiss Status
              </button>
            </div>
          )}

          {accountSwitchState.status === 'wallet_prompt_open' && (
            <div className="wallet-connector-inline-info" role="status" aria-live="polite">
              <strong>Approve in MetaMask.</strong>
              <span>Keep this tab open while you confirm the account change.</span>
            </div>
          )}

          {accountSwitchState.status === 'switching_requested' && (
            <div className="wallet-connector-inline-info" role="status" aria-live="polite">
              <strong>Request sent.</strong>
              <span>We will switch only after MetaMask confirms the active account.</span>
            </div>
          )}

          {!isSwitchingAccount && accountSwitchState.status === 'connected' && (
            <div className="wallet-connector-footnote">
              Use Request Account Change to open MetaMask. The app account changes only after provider confirmation.
            </div>
          )}

          {!isSwitchingAccount && accountSwitchState.status === 'disconnected' && (
            <div className="wallet-connector-footnote">
              Reconnect wallet to resume data exchange actions.
            </div>
          )}
        </div>
      )}
    </section>
  );
};
